import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  MapPin, Heart, MessageCircle, MessageSquare, Share2, Filter, 
  Store, Zap, Calendar, Clock, ArrowRight, Search, Briefcase, Globe, Send, User, Sparkles, TrendingUp
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

const LocalNetwork = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filtres et Tri
  const [filter, setFilter] = useState('all'); 
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [sortBy, setSortBy] = useState('recent'); // 'recent' ou 'popular'

  const [activeCommentPost, setActiveCommentPost] = useState<number | string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [likedPosts, setLikedPosts] = useState<Set<number | string>>(new Set());

  useEffect(() => {
    fetchNetworkContent();
    const savedLikes = JSON.parse(localStorage.getItem('forfeo_liked_posts') || '[]');
    setLikedPosts(new Set(savedLikes));

    const channel = supabase.channel('realtime-posts')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'posts' }, (payload) => {
        setItems(currentItems => currentItems.map(item => {
          if (item.id === payload.new.id && item.type === 'post') {
            return { ...item, likes_count: payload.new.likes_count };
          }
          return item;
        }));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchNetworkContent = async () => {
    setLoading(true);
    try {
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select(`
          id, content, image_url, created_at, likes_count,
          business:businesses(id, name, image_url, region, category),
          comments:post_comments(id, content, created_at, user_id)
        `)
        .order('created_at', { ascending: false })
        .limit(30);

      if (postsError) console.error("Erreur récupération posts:", postsError);

      const { data: deals, error: dealsError } = await supabase
        .from('last_call_deals')
        .select(`
          id, title, description, discount_price, original_price, expires_at, image_url,
          business:businesses(id, name, image_url, region)
        `)
        .gt('expires_at', new Date().toISOString())
        .order('expires_at', { ascending: true });

      if (dealsError) console.error("Erreur récupération deals:", dealsError);

      const formattedPosts = (posts || []).map(p => ({ ...p, type: 'post' }));
      const formattedDeals = (deals || []).map(d => ({ ...d, type: 'deal', created_at: d.expires_at })); 

      setItems([...formattedPosts, ...formattedDeals]);
    } catch (e) {
      console.error("Erreur feed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (item: any) => {
    if (item.type !== 'post') return;

    const postId = Number(item.id);
    const isAlreadyLiked = likedPosts.has(postId);
    const amount = isAlreadyLiked ? -1 : 1;
    const currentLikes = item.likes_count || 0;
    const newLikesCount = Math.max(0, currentLikes + amount);

    setItems(prevItems => prevItems.map(p => 
      p.id === postId && p.type === 'post' ? { ...p, likes_count: newLikesCount } : p
    ));

    const updatedLikedPosts = new Set(likedPosts);
    if (isAlreadyLiked) updatedLikedPosts.delete(postId);
    else updatedLikedPosts.add(postId);
    
    setLikedPosts(updatedLikedPosts);
    localStorage.setItem('forfeo_liked_posts', JSON.stringify(Array.from(updatedLikedPosts)));

    try {
      const { error } = await supabase.rpc('update_likes', { p_post_id: postId, p_amount: amount });
      if (error) console.error("Supabase a refusé le J'aime:", error);
    } catch (err) {
      console.error("Erreur système Like:", err);
    }
  };

  const handleShare = async (item: any) => {
    const shareUrl = `${window.location.origin}/reseau-local?post=${item.id}`;
    const titleText = item.type === 'deal' ? `Offre Flash: ${item.title}` : `Actualité de ${item.business.name}`;
    
    const shareData = {
      title: `${titleText} sur Forfeo`,
      text: `Regarde ça sur le réseau local Forfeo !`,
      url: shareUrl
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Partage annulé');
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Lien de la publication copié dans le presse-papier !');
      } catch (err) {
        console.error('Erreur copie:', err);
      }
    }
  };

  const handleSubmitComment = async (postId: string | number) => {
    if (!commentText.trim()) return;
    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert("Vous devez être connecté pour laisser un commentaire.");
        setIsSubmitting(false);
        return;
      }

      const { data: newComment, error } = await supabase
        .from('post_comments')
        .insert([{ post_id: postId, user_id: user.id, content: commentText.trim() }])
        .select()
        .single();

      if (error) throw error;

      if (newComment) {
        setItems(prevItems => prevItems.map(item => {
          if (item.id === postId && item.type === 'post') {
            return { ...item, comments: [...(item.comments || []), newComment] };
          }
          return item;
        }));
      }
      setCommentText('');
    } catch (error) {
      console.error("Erreur commentaire:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayItems = items
    .filter(item => {
      if (filter === 'deals' && item.type !== 'deal') return false;
      if (filter === 'posts' && item.type !== 'post') return false;
      if (selectedRegion !== 'all' && item.business?.region !== selectedRegion) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'popular') {
        const likesA = a.likes_count || 0;
        const likesB = b.likes_count || 0;
        if (likesB !== likesA) return likesB - likesA; 
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans">
      <Header />

      <div className="relative bg-slate-900 overflow-hidden pt-24 pb-16 px-6">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-blue-600/40 to-purple-600/40 rounded-full blur-[120px] -mr-40 -mt-40 mix-blend-screen pointer-events-none animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-emerald-500/20 to-teal-400/20 rounded-full blur-[100px] -ml-40 -mb-40 mix-blend-screen pointer-events-none"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-blue-200 text-xs font-black uppercase tracking-widest mb-6 shadow-2xl">
            <Sparkles size={14} className="text-yellow-400" />
            L'effervescence du Québec en direct
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-white drop-shadow-sm">
            Le Réseau Local
          </h1>
          <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto font-medium leading-relaxed">
            Plongez au cœur de l'actualité de vos commerçants préférés. Événements exclusifs, offres éphémères et découvertes en temps réel.
          </p>
        </div>
      </div>

      <div className="sticky top-[72px] z-40 bg-white/70 backdrop-blur-xl border-b border-white/50 shadow-[0_4px_30px_rgb(0,0,0,0.03)]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
            
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide w-full md:w-auto pb-2 md:pb-0">
                <button onClick={() => setFilter('all')} className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300 whitespace-nowrap ${filter === 'all' ? 'bg-slate-900 text-white shadow-md scale-105' : 'bg-slate-100/80 text-slate-500 hover:bg-slate-200'}`}>Tout</button>
                <button onClick={() => setFilter('deals')} className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-2 whitespace-nowrap ${filter === 'deals' ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-md shadow-red-500/20 scale-105' : 'bg-slate-100/80 text-slate-500 hover:bg-slate-200'}`}><Zap size={14}/> Offres Flash</button>
                <button onClick={() => setFilter('posts')} className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-2 whitespace-nowrap ${filter === 'posts' ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20 scale-105' : 'bg-slate-100/80 text-slate-500 hover:bg-slate-200'}`}><MessageCircle size={14}/> Actualités</button>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <div className="flex-1 md:flex-none flex items-center bg-white rounded-full px-4 py-2 shadow-sm border border-slate-200 hover:border-indigo-300 transition-colors">
                    <TrendingUp size={16} className="text-indigo-500 mr-2"/>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-transparent w-full text-xs font-black text-slate-700 outline-none uppercase tracking-wider cursor-pointer">
                        <option value="recent">Plus récents</option>
                        <option value="popular">Plus populaires 🔥</option>
                    </select>
                </div>

                <div className="flex-1 md:flex-none flex items-center bg-white rounded-full px-4 py-2 shadow-sm border border-slate-200 hover:border-blue-300 transition-colors">
                    <MapPin size={16} className="text-blue-500 mr-2"/>
                    <select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)} className="bg-transparent w-full text-xs font-black text-slate-700 outline-none uppercase tracking-wider cursor-pointer">
                        <option value="all">Toutes les régions</option>
                        <option value="Abitibi-Témiscamingue">Abitibi-Témiscamingue</option>
                        <option value="Bas-Saint-Laurent">Bas-Saint-Laurent</option>
                        <option value="Capitale-Nationale">Capitale-Nationale (Québec)</option>
                        <option value="Centre-du-Québec">Centre-du-Québec</option>
                        <option value="Charlevoix">Charlevoix</option>
                        <option value="Chaudière-Appalaches">Chaudière-Appalaches</option>
                        <option value="Côte-Nord">Côte-Nord</option>
                        <option value="Estrie">Estrie</option>
                        <option value="Gaspésie">Gaspésie–Îles-de-la-Madeleine</option>
                        <option value="Lanaudière">Lanaudière</option>
                        <option value="Laurentides">Laurentides</option>
                        <option value="Laval">Laval</option>
                        <option value="Mauricie">Mauricie</option>
                        <option value="Montérégie">Montérégie</option>
                        <option value="Montréal">Montréal</option>
                        <option value="Nord-du-Québec">Nord-du-Québec</option>
                        <option value="Outaouais">Outaouais</option>
                        <option value="Saguenay">Saguenay–Lac-Saint-Jean</option>
                    </select>
                </div>
            </div>

        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {loading ? (
            <div className="flex justify-center py-32"><div className="animate-spin rounded-full h-14 w-14 border-t-4 border-b-4 border-blue-600"></div></div>
        ) : displayItems.length === 0 ? (
            <div className="text-center py-32 bg-white rounded-[3rem] shadow-sm border border-slate-100">
                <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6"><Store size={40} className="text-slate-300"/></div>
                <p className="text-slate-500 font-bold text-lg">Le réseau est calme par ici...</p>
                <p className="text-slate-400 text-sm mt-2">Aucune publication ne correspond à vos filtres.</p>
            </div>
        ) : (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
                {displayItems.map((item) => (
                    <div key={`${item.type}-${item.id}`} className="break-inside-avoid">
                        
                        {item.type === 'deal' ? (
                            <div className="bg-white rounded-[2.5rem] overflow-hidden border border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500 group cursor-pointer" onClick={() => navigate(`/place/${item.business.id}`)}>
                                <div className="relative p-2">
                                    <div className="w-full h-56 rounded-[2rem] overflow-hidden relative">
                                        <img src={item.image_url || "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da"} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={item.title}/>
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent"></div>
                                        
                                        <div className="absolute top-4 right-4 bg-red-500/90 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest flex items-center gap-1.5 shadow-lg border border-white/20 animate-pulse">
                                            <Clock size={12}/> Last Call
                                        </div>
                                        
                                        <div className="absolute bottom-0 left-0 right-0 p-6">
                                            <h3 className="text-white font-black text-xl leading-tight mb-2 drop-shadow-md">{item.title}</h3>
                                            <div className="flex items-center gap-3">
                                                <span className="bg-white text-slate-900 px-3 py-1 rounded-xl font-black text-xl shadow-md">{item.discount_price}$</span>
                                                <span className="text-white/70 text-sm font-bold line-through decoration-red-500">{item.original_price}$</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6 pt-4">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden ring-4 ring-white shadow-sm shrink-0">
                                            {item.business.image_url ? <img src={item.business.image_url} className="w-full h-full object-cover"/> : <Store size={20} className="m-2.5 text-slate-400"/>}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-black text-slate-900 uppercase truncate">{item.business.name}</p>
                                            <p className="text-[10px] text-slate-500 font-bold flex items-center gap-1 uppercase tracking-widest"><MapPin size={10}/> {item.business.region}</p>
                                        </div>
                                    </div>
                                    <p className="text-sm text-slate-600 mb-6 line-clamp-2 font-medium">{item.description}</p>
                                    <div className="flex gap-2">
                                        <button className="flex-1 bg-slate-900 text-white py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-600 transition-colors shadow-md flex items-center justify-center gap-2">
                                            Profiter <ArrowRight size={14}/>
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); handleShare(item); }} className="w-12 h-12 flex items-center justify-center bg-slate-100 text-slate-500 rounded-2xl hover:bg-slate-200 hover:text-slate-900 transition-colors">
                                            <Share2 size={18}/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-[2.5rem] overflow-hidden border border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500 group">
                                <div className="p-6 pb-4 flex items-center justify-between">
                                    <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate(`/place/${item.business.id}`)}>
                                        <div className="w-12 h-12 rounded-full bg-slate-100 overflow-hidden ring-4 ring-white shadow-md shrink-0 relative">
                                            {item.business.image_url ? <img src={item.business.image_url} className="w-full h-full object-cover"/> : <Store size={24} className="m-3 text-slate-400"/>}
                                        </div>
                                        <div>
                                            <p className="text-sm font-black text-slate-900 uppercase hover:text-blue-600 transition-colors">{item.business.name}</p>
                                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                                                {new Date(item.created_at).toLocaleDateString()} • {item.business.region}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <button 
                                      onClick={() => handleShare(item)}
                                      className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                    >
                                      <Share2 size={18}/>
                                    </button>
                                </div>
                                
                                {item.image_url && (
                                    <div className="w-full h-72 bg-slate-100 overflow-hidden cursor-pointer relative px-2" onClick={() => navigate(`/place/${item.business.id}`)}>
                                        <div className="w-full h-full rounded-[2rem] overflow-hidden relative shadow-inner">
                                            <img src={item.image_url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent"></div>
                                        </div>
                                    </div>
                                )}

                                <div className="p-6 pt-4">
                                    <p className="text-slate-700 text-[15px] leading-relaxed font-medium whitespace-pre-line mb-6">
                                        {item.content}
                                    </p>
                                    
                                    <div className="flex items-center gap-2 pt-4 border-t border-slate-100/80">
                                        <button 
                                          onClick={() => handleLike(item)}
                                          className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all text-xs font-black ${likedPosts.has(item.id) ? 'bg-red-50 text-red-500' : 'bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}
                                        >
                                            <Heart size={14} className={`${likedPosts.has(item.id) ? 'fill-current scale-110 animate-pulse' : ''}`}/> 
                                            {item.likes_count > 0 ? item.likes_count : 'J\'aime'}
                                        </button>
                                        
                                        <button 
                                          className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all text-xs font-black ${activeCommentPost === item.id ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}
                                          onClick={() => setActiveCommentPost(activeCommentPost === item.id ? null : item.id)}
                                        >
                                            <MessageCircle size={14}/> 
                                            {item.comments && item.comments.length > 0 ? item.comments.length : 'Commenter'}
                                        </button>

                                        <button 
                                          className="flex items-center gap-2 px-3 py-2 rounded-full transition-all text-xs font-black bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-900 ml-auto"
                                          onClick={() => navigate(`/place/${item.business.id}?chat=true`)}
                                        >
                                            <MessageSquare size={14}/> 
                                            Contacter
                                        </button>
                                    </div>

                                    {activeCommentPost === item.id && (
                                        <div className="mt-6 pt-6 border-t border-slate-100 animate-in fade-in slide-in-from-top-4 duration-500">
                                            {item.comments && item.comments.length > 0 && (
                                                <div className="mb-6 space-y-4 max-h-60 overflow-y-auto pr-2 scrollbar-hide">
                                                    {item.comments.map((comment: any) => (
                                                        <div key={comment.id} className="flex gap-3">
                                                            <div className="w-8 h-8 rounded-full bg-slate-200 overflow-hidden flex-shrink-0 shadow-sm border border-white mt-1">
                                                                <div className="w-full h-full flex items-center justify-center bg-slate-100"><User size={14} className="text-slate-400" /></div>
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="bg-[#F1F5F9] px-4 py-3 rounded-2xl rounded-tl-sm inline-block">
                                                                    <p className="text-[11px] font-black text-slate-900 mb-1">Membre Forfeo</p>
                                                                    <p className="text-sm text-slate-700 font-medium">{comment.content}</p>
                                                                </div>
                                                                <p className="text-[10px] text-slate-400 mt-1.5 ml-2 font-bold uppercase tracking-widest">
                                                                    {new Date(comment.created_at).toLocaleDateString()}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="flex items-end gap-3 bg-[#F1F5F9] p-2 rounded-[1.5rem] border border-slate-200 focus-within:border-blue-300 focus-within:bg-white transition-colors">
                                                <div className="w-8 h-8 rounded-full bg-slate-200 shrink-0 ml-1 mb-1 overflow-hidden flex items-center justify-center">
                                                    <User size={14} className="text-slate-400" />
                                                </div>
                                                <textarea 
                                                  value={commentText} 
                                                  onChange={(e) => setCommentText(e.target.value)} 
                                                  placeholder="Ajouter un commentaire..." 
                                                  className="flex-1 bg-transparent border-none focus:ring-0 text-sm font-medium resize-none max-h-24 outline-none py-2.5" 
                                                  rows={1}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleSubmitComment(item.id);
                                                    }
                                                  }}
                                                />
                                                <button 
                                                  onClick={() => handleSubmitComment(item.id)} 
                                                  disabled={!commentText.trim() || isSubmitting} 
                                                  className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-50 mb-0.5 shrink-0 shadow-md shadow-blue-500/20"
                                                >
                                                    {isSubmitting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> : <Send size={16} className="ml-1" />}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default LocalNetwork;
